<template>
  <Modal title="" :max-width="720" :header-border="false" body-overflow="auto" body-max-height="600px" :body-padding="false" @close="onClose">
    <template #header>
      <div class="cd__title-wrap">
        <span class="cd-source">
          <span class="cd-source__mark" style="background: #0d9dda; color: #fff;">
            <svg viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 4.2a3 3 0 015.4 1 2.4 2.4 0 011.6 4.3 2.6 2.6 0 01-3 2 3.1 3.1 0 01-5.2-.4 2.3 2.3 0 01-2-3.9 2.7 2.7 0 013.2-3z"/></svg>
          </span>
          Salesforce · {{ prettyObjectName }}
        </span>
        <div class="cd__name">
          {{ recordName }}
          <a v-if="recordUrl" :href="recordUrl" target="_blank" rel="noopener" class="cd-arrow" :title="`Open in Salesforce`"><AppIcon name="arrow_up_right" :size="14" /></a>
        </div>
        <div v-if="highlightChips.length" class="cd__sub">
          <template v-for="(chip, i) in highlightChips" :key="chip.k">
            <span v-if="i > 0" class="cd__dot" />
            <span>{{ chip.v }}</span>
          </template>
        </div>
      </div>
    </template>

    <div class="cd-cols">
      <!-- LEFT -->
      <div class="cd-cols__l">
        <div v-if="metrics.length" class="cd-metrics">
          <template v-for="(m, i) in metrics" :key="m.k">
            <div class="cd-vrule" v-if="i > 0" />
            <div class="cd-metric">
              <span class="cd-metric__k">{{ m.k }}</span>
              <span class="cd-metric__v">{{ m.v }}</span>
            </div>
          </template>
        </div>

        <div v-if="notes.length" class="cd-notes">
          <div v-for="n in notes" :key="n.k" class="cd-note">
            <span class="cd-note__k">{{ n.k }}</span>
            <span class="cd-note__v">{{ n.v }}</span>
          </div>
        </div>

        <div v-if="fields.length" class="cd-fields">
          <div v-for="f in fields" :key="f.k" class="cd-field">
            <span class="cd-field__k">{{ f.k }}</span>
            <span class="cd-field__v">{{ f.v }}</span>
          </div>
        </div>

        <div v-if="!metrics.length && !notes.length && !fields.length" class="cd-note__empty">No fields to display.</div>
      </div>

      <!-- RIGHT -->
      <div class="cd-cols__r">
        <div v-if="company">
          <div class="cd-label">Account</div>
          <div class="cd-assoc">
            <CompanyLogo :domain="company.domain" :name="company.name" aria-hidden="true" />
            <div style="flex: 1; min-width: 0;">
              <div class="cd-assoc__v">
                {{ company.name }}
                <a v-if="accountUrl" :href="accountUrl" target="_blank" rel="noopener" class="cd-arrow" title="Open in Salesforce"><AppIcon name="arrow_up_right" :size="12" /></a>
              </div>
            </div>
          </div>
        </div>

        <div v-if="lookups.length">
          <div class="cd-label">Related</div>
          <ul class="co-lookup-list">
            <li v-for="lu in lookups" :key="lu.k" class="co-lookup">
              <span class="co-lookup__k">{{ lu.k }}</span>
              <a v-if="lu.url" :href="lu.url" target="_blank" rel="noopener" class="cd-arrow" :title="`Open ${lu.k} in Salesforce`"><AppIcon name="arrow_up_right" :size="12" /></a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <template #footer>
      <button class="cd-btn cd-btn--ghost" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<style scoped>
.co-lookup-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.co-lookup {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) 0;
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  font-weight: var(--fw-medium);
}

.co-lookup + .co-lookup {
  border-top: 1px solid var(--color-border-subtle);
}

.co-lookup__k {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import Modal from '../../next/components/Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import CompanyLogo from '../../next/components/CompanyLogo.vue'
import { getSalesforceUrl } from '../../services/salesforce'
import { useAccountBasics } from '../../hooks/useAccountBasics'

type FieldMetaItem = { name: string; label: string; type: string | null; referenceTo: string[] | null }
type FieldMeta = { label: string; type: string | null; referenceTo: string[] | null }

const props = defineProps<{
  record: Record<string, any>
  objectName: string
  accountId?: string
  // Preloaded field metadata from the parent (drawer card); when present we
  // skip the /context_config fetch entirely, saving a live SF describe call.
  fieldMeta?: FieldMetaItem[] | null
  onClose: () => void
}>()

function metaListToMap(list: FieldMetaItem[] | null | undefined): Record<string, FieldMeta> {
  const out: Record<string, FieldMeta> = {}
  if (!Array.isArray(list)) return out
  for (const f of list) {
    if (!f || typeof f !== 'object') continue
    const name = String(f.name || '').trim()
    if (!name) continue
    out[name] = {
      label: String(f.label || name),
      type: (typeof f.type === 'string' && f.type) ? f.type : null,
      referenceTo: Array.isArray(f.referenceTo) ? f.referenceTo.map((x) => String(x)) : null,
    }
  }
  return out
}

const fieldMeta = ref<Record<string, FieldMeta>>(metaListToMap(props.fieldMeta))

const STANDARD_LABELS: Record<string, string> = {
  Id: 'Id',
  Name: 'Name',
  CreatedDate: 'Created',
  LastModifiedDate: 'Last modified',
  CreatedById: 'Created by',
  LastModifiedById: 'Modified by',
  OwnerId: 'Owner',
  RecordTypeId: 'Record type',
  SystemModstamp: 'System modstamp',
}

// Fields that never appear in the body — either used elsewhere (Name in the
// title, CreatedDate / LastModifiedDate as metrics, AccountId in the right
// column) or purely internal (SF metadata envelope).
const ALWAYS_HIDE = new Set(['attributes', 'object_name', 'Name', 'Id', 'CreatedDate', 'LastModifiedDate', 'AccountId'])

// Header-subtitle candidates: enum-ish fields with short, human-meaningful
// values. First 3 that exist and have a non-empty value are shown.
const HIGHLIGHT_KEYS = ['Status', 'Stage', 'StageName', 'Type', 'Priority', 'State']

// Standard system Id fields whose SF describe metadata we don't fetch (only
// __c fields come back in available_fields). Hardcoded target object types so
// they still get proper Lightning URLs.
const SYSTEM_ID_TARGETS: Record<string, string> = {
  OwnerId: 'User',
  CreatedById: 'User',
  LastModifiedById: 'User',
  RecordTypeId: 'RecordType',
}

const NOTES_MIN_LEN = 100

const accountIdRef = computed(() => props.accountId || '')
const { basics } = useAccountBasics(toRef(accountIdRef))

const prettyObjectName = computed(() =>
  String(props.objectName || '').replace(/__c$/, '').replace(/_/g, ' '),
)

const recordName = computed(() =>
  String(props.record?.Name || props.record?.name || prettyObjectName.value || 'Record'),
)

const recordUrl = computed(() => {
  const id = props.record?.Id || props.record?.id
  if (!id) return null
  return getSalesforceUrl({ Id: id }, String(props.objectName || ''))
})

const accountUrl = computed(() => {
  const sfId = basics.value?.salesforce_account_id
  return sfId ? getSalesforceUrl({ Id: sfId }, 'Account') : null
})

const company = computed(() => {
  const b = basics.value
  if (!b) return null
  return { name: b.name || b.domain, domain: b.domain }
})

function humanize(name: string): string {
  return String(name || '')
    .replace(/__c$/, '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function labelFor(key: string): string {
  return STANDARD_LABELS[key] || fieldMeta.value[key]?.label || humanize(key)
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === ''
}

function isReferenceField(key: string): boolean {
  if (SYSTEM_ID_TARGETS[key]) return true
  return fieldMeta.value[key]?.type === 'reference'
}

function referenceTarget(key: string): string | null {
  const sys = SYSTEM_ID_TARGETS[key]
  if (sys) return sys
  const refs = fieldMeta.value[key]?.referenceTo
  return refs && refs.length > 0 ? refs[0] : null
}

function formatDate(v: unknown): string {
  const s = String(v || '')
  const t = Date.parse(s)
  if (!Number.isFinite(t)) return s
  return new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatValue(key: string, v: unknown): string {
  if (isEmpty(v)) return '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'object') return JSON.stringify(v)
  const t = fieldMeta.value[key]?.type
  const s = String(v)
  if (t === 'date' || t === 'datetime') return formatDate(s)
  return s
}

const highlightChips = computed(() => {
  const out: { k: string; v: string }[] = []
  for (const key of HIGHLIGHT_KEYS) {
    if (out.length >= 3) break
    const v = (props.record as any)?.[key]
    if (isEmpty(v)) continue
    out.push({ k: key, v: String(v) })
  }
  return out
})

const highlightKeySet = computed(() => new Set(highlightChips.value.map((c) => c.k)))

type Metric = { k: string; v: string }

const metrics = computed<Metric[]>(() => {
  const rec = (props.record || {}) as Record<string, any>
  const out: Metric[] = []
  if (!isEmpty(rec.CreatedDate)) out.push({ k: 'Created', v: formatDate(rec.CreatedDate) })
  if (!isEmpty(rec.LastModifiedDate)) out.push({ k: 'Last modified', v: formatDate(rec.LastModifiedDate) })
  return out
})

const notes = computed(() => {
  const rec = (props.record || {}) as Record<string, any>
  const out: { k: string; v: string }[] = []
  for (const [k, v] of Object.entries(rec)) {
    if (ALWAYS_HIDE.has(k) || highlightKeySet.value.has(k)) continue
    if (isReferenceField(k)) continue
    if (typeof v !== 'string') continue
    if (v.length <= NOTES_MIN_LEN) continue
    out.push({ k: labelFor(k), v })
  }
  return out
})

const notesKeySet = computed(() => {
  const rec = (props.record || {}) as Record<string, any>
  const set = new Set<string>()
  for (const [k, v] of Object.entries(rec)) {
    if (typeof v === 'string' && v.length > NOTES_MIN_LEN) set.add(k)
  }
  return set
})

const fields = computed(() => {
  const rec = (props.record || {}) as Record<string, any>
  const out: { k: string; v: string }[] = []
  for (const [k, v] of Object.entries(rec)) {
    if (ALWAYS_HIDE.has(k)) continue
    if (highlightKeySet.value.has(k)) continue
    if (notesKeySet.value.has(k)) continue
    if (isReferenceField(k)) continue
    out.push({ k: labelFor(k), v: formatValue(k, v) })
  }
  return out
})

type Lookup = { k: string; url: string | null }

const lookups = computed<Lookup[]>(() => {
  const rec = (props.record || {}) as Record<string, any>
  const out: Lookup[] = []
  for (const [k, v] of Object.entries(rec)) {
    if (ALWAYS_HIDE.has(k)) continue
    if (!isReferenceField(k)) continue
    if (isEmpty(v)) continue
    const target = referenceTarget(k)
    if (!target) continue
    out.push({ k: labelFor(k), url: getSalesforceUrl({ Id: String(v) }, target) })
  }
  return out
})

</script>
